import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import { useEditorRemixLifecycleOwner } from '@atlassian/malleable-ui-components/editor-remix/lifecycle-owner';

import { observeMauiEmbedRemoval } from '../pm-plugins/maui-diagram-paste-plugin';

type RecoveryBinding = {
	contentId: string;
	lifecycle: NonNullable<NonNullable<ReturnType<typeof useEditorRemixLifecycleOwner>>['active']>;
	token: symbol;
	unobserve: () => void;
};

type EditorRemixRecoveryCoordinator = {
	defer: (contentId: string) => boolean;
	discard: (contentId: string) => boolean;
	resolve: (contentId: string) => boolean;
};

const EditorRemixRecoveryContext = createContext<EditorRemixRecoveryCoordinator | undefined>(
	undefined,
);

export const useEditorRemixRecoveryCoordinator = (): EditorRemixRecoveryCoordinator | undefined =>
	useContext(EditorRemixRecoveryContext);

export const EditorRemixRecoveryCoordinatorProvider = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => {
	const owner = useEditorRemixLifecycleOwner();
	const activeRef = useRef(owner?.active);
	const bindingRef = useRef<RecoveryBinding | undefined>(undefined);
	activeRef.current = owner?.active;
	const activeToken = owner?.active?.token;

	/** Drops a binding whose flow is no longer the owner's active flow; its observer must not linger. */
	const dropStaleBinding = useCallback(() => {
		const binding = bindingRef.current;
		if (binding && binding.token !== activeRef.current?.token) {
			bindingRef.current = undefined;
			binding.unobserve();
		}
	}, []);

	useEffect(() => {
		dropStaleBinding();
	}, [activeToken, dropStaleBinding]);

	const consumeExactBinding = useCallback((contentId: string): RecoveryBinding | undefined => {
		const binding = bindingRef.current;
		const active = activeRef.current;
		if (!binding || !active || binding.contentId !== contentId || binding.token !== active.token) {
			return undefined;
		}
		bindingRef.current = undefined;
		binding.unobserve();
		return binding;
	}, []);

	const discard = useCallback(
		(contentId: string): boolean => {
			const binding = consumeExactBinding(contentId);
			if (!binding?.lifecycle) {
				return false;
			}
			binding.lifecycle.transition({ type: 'INSERTION_FAILED' });
			binding.lifecycle.end('error');
			return true;
		},
		[consumeExactBinding],
	);

	const defer = useCallback(
		(contentId: string): boolean => {
			const lifecycle = activeRef.current;
			if (!lifecycle) {
				return false;
			}
			dropStaleBinding();
			const existing = bindingRef.current;
			if (existing) {
				// One recovery embed per flow. Repeating the same id is idempotent; a different id means
				// the flow's recovery embed moved, so the old observer is released and the id rebound.
				if (existing.contentId === contentId) {
					return true;
				}
				bindingRef.current = undefined;
				existing.unobserve();
			}
			// Observe before claiming ownership: if the embed is already gone, the streamed content was
			// not inserted, so settle now rather than retain a handle nothing can release.
			const unobserve = observeMauiEmbedRemoval(contentId, () => discard(contentId));
			if (!unobserve) {
				lifecycle.transition({ type: 'INSERTION_FAILED' });
				lifecycle.end('error');
				return false;
			}
			lifecycle.setOwnerDestructionEndReason('error');
			bindingRef.current = { contentId, lifecycle, token: lifecycle.token, unobserve };
			return true;
		},
		[discard, dropStaleBinding],
	);

	const resolve = useCallback(
		(contentId: string): boolean => {
			const binding = consumeExactBinding(contentId);
			if (!binding?.lifecycle) {
				return false;
			}
			// The owner releases itself only on an accepted `INSERTED`. A rejected transition
			// (a concurrent terminal signal already moved the machine on) must not strand ownership.
			if (!binding.lifecycle.transition({ type: 'INSERTED' })) {
				binding.lifecycle.rollback();
			}
			return true;
		},
		[consumeExactBinding],
	);

	useEffect(
		() => () => {
			const binding = bindingRef.current;
			if (!binding) {
				return;
			}
			bindingRef.current = undefined;
			binding.unobserve();
			if (activeRef.current?.token === binding.token) {
				binding.lifecycle.transition({ type: 'INSERTION_FAILED' });
				binding.lifecycle.end('error');
			}
		},
		[],
	);

	const value = useMemo(() => ({ defer, discard, resolve }), [defer, discard, resolve]);

	return (
		<EditorRemixRecoveryContext.Provider value={value}>
			{children}
		</EditorRemixRecoveryContext.Provider>
	);
};
